/**
 * Defensive helpers for reading JSON from a fetch Response.
 *
 * Why this exists: framework-level 500s (Vercel function timeout, an OOM from
 * sharp on a large upload, an edge crash before the route handler runs) return
 * a plain-text "Internal Server Error" body, NOT JSON. Calling `response.json()`
 * on those throws a `SyntaxError: ... is not valid JSON`, which masks the real
 * HTTP status and surfaces in PostHog Error Tracking as a bogus client crash on
 * the grass-ID flow. Reading the body once and parsing defensively lets callers
 * branch on `response.ok` / `response.status` instead of crashing on the body.
 */

/**
 * Parse a Response body as JSON without throwing on non-JSON (or empty) bodies.
 * Returns `null` when the body is missing or not valid JSON — callers should
 * fall back to `response.status` for the error message in that case.
 */
export async function safeJson<T = unknown>(response: Response): Promise<T | null> {
  // Prefer reading the raw body as text so a non-JSON error page (a plain-text
  // 500) doesn't throw the way response.json() would. Fall back to
  // response.json() only when text() is unavailable (e.g. a partial Response
  // mock); those callers always return valid JSON, so json() is safe there.
  if (typeof response.text === 'function') {
    let text: string
    try {
      text = await response.text()
    } catch {
      return null
    }
    if (!text) return null
    try {
      return JSON.parse(text) as T
    } catch {
      return null
    }
  }

  if (typeof response.json === 'function') {
    try {
      return (await response.json()) as T
    } catch {
      return null
    }
  }

  return null
}

/**
 * Coerce an unknown API `error` field into a human-readable string for
 * `new Error(...)`. APIs occasionally return `{ error: { ... } }` instead of a
 * string; `new Error(object)` stringifies to the useless `"[object Object]"`,
 * which is exactly what showed up in Error Tracking. This unwraps a nested
 * `.message`, accepts plain strings, and otherwise uses the caller's fallback.
 */
export function errorMessageFrom(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value.trim()) return value
  if (value && typeof value === 'object') {
    const nested = (value as { message?: unknown; error?: unknown }).message
      ?? (value as { error?: unknown }).error
    if (typeof nested === 'string' && nested.trim()) return nested
  }
  return fallback
}
